import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { LocalDate } from "@js-joda/core";
import {
  buildCalendarDays,
  buildDateArray,
  DatePickerDateFormat,
  formatDateValue,
  formatMultiDateDisplay,
  getStockholmLocalDateNow,
  getCalendarLabels,
  isSameMonthYear,
  isWeekend,
  normalizeToFirstOfMonth,
  parseDayKey,
  parseInputDate,
  toDayKey,
} from "./datePicker.utils";
import { useNonWorkingDays } from "./hooks/useNonWorkingDays";
import {
  DatePickerProps,
  DatePickerSize,
  DatePickerState,
  SelectionMode,
} from "./datePicker.types";
import { useDatePickerState } from "./useDatePickerState";
import { Container } from "./datePicker.styles";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { DatePickerPopup } from "./datePickerPopup";
import { DatePickerInput } from "./datePickerInput";

const DEFAULT_MENU_Z_INDEX = 6000;
const DEFAULT_POPUP_MIN_WIDTH_PX = 360;
const COMPACT_POPUP_MIN_WIDTH_PX = 320;

const addMonths = (base: LocalDate, deltaMonths: number): LocalDate => {
  const totalMonths = base.year() * 12 + (base.monthValue() - 1) + deltaMonths;
  const year = Math.floor(totalMonths / 12);
  const monthIndex = totalMonths - year * 12;
  return LocalDate.of(year, monthIndex + 1, 1);
};

export const DatePicker = (props: DatePickerProps): JSX.Element => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();

  const dateFormat: DatePickerDateFormat = props.dateFormat ?? "YYYY-MM-DD";
  const size: DatePickerSize = props.size ?? "default";
  const allowRange =
    Boolean(props.allowRangePicker) || Boolean(props.rangeOnly);

  const allowSingleModeMultiSelect = props.allowMultiSelectInSingleMode ?? true;

  const initialMode: SelectionMode = props.rangeOnly ? "range" : "single";
  const baseDate =
    props.value ?? props.restrictToMonth ?? getStockholmLocalDateNow();
  const hook = useDatePickerState(baseDate, initialMode);

  const minDate = useMemo(
    () => props.earliestSelectableDate ?? null,
    [props.earliestSelectableDate],
  );
  const isAllowedByMin = (d: LocalDate): boolean => {
    if (!minDate) return true;
    return !d.isBefore(minDate);
  };

  const monthlyDay =
    typeof props.monthlySelectableDay === "number" &&
    props.monthlySelectableDay >= 1 &&
    props.monthlySelectableDay <= 31
      ? props.monthlySelectableDay
      : null;
  const matchesMonthlyDay = (d: LocalDate): boolean =>
    monthlyDay ? d.dayOfMonth() === monthlyDay : true;

  const restrictSameMonthRange = props.restrictToMonth != null;

  const isControlledHoliday = typeof props.excludeHolidays === "boolean";
  const isControlledWeekends = typeof props.excludeWeekends === "boolean";
  const legacyExclude =
    !isControlledHoliday &&
    !isControlledWeekends &&
    props.showExcludeToggles !== true
      ? Boolean(props.excludeNonWorkingDays)
      : false;

  const excludeHolidaysValue =
    (isControlledHoliday
      ? Boolean(props.excludeHolidays)
      : hook.state.localExcludeHolidays) || legacyExclude;
  const excludeWeekendsValue =
    (isControlledWeekends
      ? Boolean(props.excludeWeekends)
      : hook.state.localExcludeWeekends) || legacyExclude;

  const companyId = companyState.company?.id ?? null;
  const year = hook.state.currentMonth.year();
  const nonWorkSet = useNonWorkingDays({
    companyId,
    year,
    includeWeekends: Boolean(excludeWeekendsValue),
  });

  const shouldExclude = (d: LocalDate): boolean => {
    if (!allowRange || hook.state.mode !== "range") return false;
    if (excludeWeekendsValue && isWeekend(d)) return true;
    if (excludeHolidaysValue && nonWorkSet.has(toDayKey(d))) return true;
    return false;
  };

  const days = useMemo(() => {
    return buildCalendarDays(hook.state.currentMonth);
  }, [hook.state.currentMonth]);

  const calendarLabels = useMemo(() => getCalendarLabels(t), [t]);

  const todayKey = useMemo(() => toDayKey(getStockholmLocalDateNow()), []);
  const valueKey = useMemo(
    () => (props.value ? toDayKey(props.value) : null),
    [props.value],
  );
  const rangeStartKey = useMemo(
    () => (hook.state.range.start ? toDayKey(hook.state.range.start) : null),
    [hook.state.range.start],
  );
  const rangeEndKey = useMemo(
    () => (hook.state.range.end ? toDayKey(hook.state.range.end) : null),
    [hook.state.range.end],
  );
  const pendingStartKey = useMemo(
    () => (hook.state.pendingStart ? toDayKey(hook.state.pendingStart) : null),
    [hook.state.pendingStart],
  );

  const getDraftDisplay = (): string => {
    if (!allowRange) return formatDateValue(props.value ?? null, dateFormat);
    if (hook.state.mode === "range") {
      return hook.state.range.start && hook.state.range.end
        ? `${formatDateValue(hook.state.range.start, dateFormat)} – ${formatDateValue(
            hook.state.range.end,
            dateFormat,
          )}`
        : hook.state.range.start
          ? `${formatDateValue(hook.state.range.start, dateFormat)} – …`
          : "";
    }
    if (!allowSingleModeMultiSelect) {
      return formatDateValue(props.value ?? null, dateFormat);
    }
    return formatMultiDateDisplay(hook.state.multiSelectedKeys, dateFormat);
  };

  const getCommittedDisplay = (): string => {
    if (!allowRange) return formatDateValue(props.value ?? null, dateFormat);
    if (hook.state.mode === "range") {
      return hook.state.committedRange.start && hook.state.committedRange.end
        ? `${formatDateValue(
            hook.state.committedRange.start,
            dateFormat,
          )} – ${formatDateValue(hook.state.committedRange.end, dateFormat)}`
        : "";
    }
    if (!allowSingleModeMultiSelect) {
      return formatDateValue(props.value ?? null, dateFormat);
    }
    return formatMultiDateDisplay(hook.state.committedMultiKeys, dateFormat);
  };

  const showValue = hook.state.isOpen
    ? getDraftDisplay()
    : getCommittedDisplay();
  const placeholderText = t("DatePicker.Placeholder");
  const isFilled = allowRange
    ? showValue.length > 0
    : (hook.state.isFocused ? hook.state.draftValue : showValue).trim().length >
      0;
  const resolvedErrorText =
    props.errorText ?? t("DesignComponents.DefaultError");

  const visualState = useMemo<DatePickerState>(() => {
    if (props.disabled) return "disabled";
    if (props.hasError) return "error";
    if (hook.state.isOpen) return "active";
    if (isFilled) return "filled";
    return "inactive";
  }, [hook.state.isOpen, isFilled, props.disabled, props.hasError]);

  const showModeButtons = allowRange && !props.rangeOnly;
  const showHolidayWeekendToggles =
    allowRange &&
    hook.state.mode === "range" &&
    props.showExcludeToggles === true;
  const showClearButton =
    Boolean(props.allowClear) && !props.disabled && isFilled;

  const canConfirm = useMemo(() => {
    if (hook.state.mode === "range")
      return Boolean(hook.state.range.start && hook.state.range.end);
    if (allowRange && allowSingleModeMultiSelect)
      return hook.state.multiSelectedKeys.size > 0 || Boolean(props.value);
    if (allowRange) return Boolean(props.value);
    return Boolean(props.value);
  }, [
    allowRange,
    allowSingleModeMultiSelect,
    hook.state.mode,
    hook.state.multiSelectedKeys,
    props.value,
    hook.state.range,
  ]);

  const handleConfirm = (): void => {
    if (props.disabled || !canConfirm) return;
    let selectedDates: LocalDate[] = [];
    if (
      hook.state.mode === "range" &&
      hook.state.range.start &&
      hook.state.range.end
    ) {
      selectedDates = buildDateArray(
        hook.state.range.start,
        hook.state.range.end,
      ).filter(
        (d) => isAllowedByMin(d) && !shouldExclude(d) && matchesMonthlyDay(d),
      );
    } else if (allowRange) {
      const keys = Array.from(hook.state.multiSelectedKeys);
      const datesFromKeys =
        keys.length > 0
          ? keys.map(parseDayKey).sort((a, b) => a.compareTo(b))
          : [];
      const base =
        datesFromKeys.length > 0
          ? datesFromKeys
          : props.value
            ? [props.value]
            : [];
      selectedDates = base.filter(
        (d) => isAllowedByMin(d) && !shouldExclude(d) && matchesMonthlyDay(d),
      );
    }

    if (hook.state.mode === "range") {
      if (hook.state.range.start && hook.state.range.end) {
        hook.dispatch({
          type: "set-committed-range",
          committedRange: {
            start: hook.state.range.start,
            end: hook.state.range.end,
          },
        });
        hook.dispatch({
          type: "set-committed-multi",
          committedMultiKeys: new Set(),
        });
      }
    } else if (allowRange) {
      hook.dispatch({
        type: "set-committed-multi",
        committedMultiKeys: new Set(selectedDates.map((d) => toDayKey(d))),
      });
      hook.dispatch({
        type: "set-committed-range",
        committedRange: { start: null, end: null },
      });
    }

    if (props.onRangeChange) {
      props.onRangeChange(selectedDates);
    } else if (!allowRange) {
      props.onChange?.(selectedDates[0] ?? null);
    }

    hook.dispatch({ type: "set-open", isOpen: false });
    props.onBlur?.();
  };

  const closePicker = (commitIfPossible: boolean): void => {
    if (commitIfPossible && allowRange && canConfirm) {
      handleConfirm();
      return;
    }
    hook.dispatch({ type: "set-open", isOpen: false });
    hook.dispatch({ type: "set-focused", isFocused: false });
    props.onBlur?.();
  };

  const handleToggleOpen = (): void => {
    if (props.disabled) return;
    if (hook.state.isOpen) {
      closePicker(true);
      return;
    }
    hook.dispatch({ type: "set-open", isOpen: true });
    props.onFocus?.();
  };

  const handleFocus = (): void => {
    if (props.disabled) return;
    hook.dispatch({ type: "set-focused", isFocused: true });
    props.onFocus?.();
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    hook.dispatch({ type: "set-draft-value", draftValue: event.target.value });
  };

  const handleInputBlur = (): void => {
    hook.dispatch({ type: "set-focused", isFocused: false });
    if (props.disabled || allowRange) return;
    const parsed = parseInputDate(hook.state.draftValue, dateFormat);
    if (!parsed) {
      hook.dispatch({
        type: "set-draft-value",
        draftValue: formatDateValue(props.value ?? null, dateFormat),
      });
      props.onBlur?.();
      return;
    }
    if (!isAllowedByMin(parsed) || !matchesMonthlyDay(parsed)) {
      hook.dispatch({
        type: "set-draft-value",
        draftValue: formatDateValue(props.value ?? null, dateFormat),
      });
      props.onBlur?.();
      return;
    }
    props.onChange?.(parsed);
    hook.dispatch({
      type: "set-draft-value",
      draftValue: formatDateValue(parsed, dateFormat),
    });
    hook.dispatch({ type: "set-open", isOpen: false });
    props.onBlur?.();
  };

  const handleClear = (event?: React.MouseEvent): void => {
    if (!props.allowClear || props.disabled) return;
    event?.stopPropagation();
    hook.dispatch({ type: "reset-selection" });
    if (allowRange) {
      if (props.onRangeChange) props.onRangeChange([]);
      else props.onChange?.(null);
    } else {
      props.onChange?.(null);
    }
    hook.dispatch({ type: "set-open", isOpen: false });
    props.onBlur?.();
  };

  const handleDateSelectSingleImmediate = (date: LocalDate): void => {
    if (props.disabled) return;
    if (!isAllowedByMin(date)) return;
    if (!matchesMonthlyDay(date)) return;
    props.onChange?.(date);
    hook.dispatch({ type: "set-open", isOpen: false });
    hook.dispatch({
      type: "set-draft-value",
      draftValue: formatDateValue(date, dateFormat),
    });
    props.onBlur?.();
  };

  const handleDateSelectSingleInRangePicker = (date: LocalDate): void => {
    if (props.disabled) return;
    if (!isAllowedByMin(date)) return;
    if (!matchesMonthlyDay(date)) return;
    props.onRangeChange?.([date]);
    hook.dispatch({
      type: "set-committed-range",
      committedRange: { start: date, end: date },
    });
    hook.dispatch({
      type: "set-committed-multi",
      committedMultiKeys: new Set([toDayKey(date)]),
    });
    hook.dispatch({ type: "set-open", isOpen: false });
    props.onBlur?.();
  };

  const handleDateSelectRange = (date: LocalDate): void => {
    if (props.disabled) return;
    if (!isAllowedByMin(date)) return;
    if (!matchesMonthlyDay(date)) return;

    if (!hook.state.pendingStart) {
      hook.dispatch({ type: "set-pending-start", pendingStart: date });
      hook.dispatch({ type: "set-range", range: { start: date, end: null } });
      return;
    }

    if (
      restrictSameMonthRange &&
      !isSameMonthYear(date, hook.state.pendingStart)
    ) {
      hook.dispatch({ type: "set-pending-start", pendingStart: date });
      hook.dispatch({ type: "set-range", range: { start: date, end: null } });
      return;
    }

    const start = hook.state.pendingStart;
    if (date.isBefore(start)) {
      hook.dispatch({ type: "set-range", range: { start: date, end: start } });
    } else {
      hook.dispatch({ type: "set-range", range: { start, end: date } });
    }
    hook.dispatch({ type: "set-pending-start", pendingStart: null });
  };

  const handleToggleMode = (next: SelectionMode): void => {
    hook.dispatch({ type: "set-mode", mode: next });
    hook.dispatch({ type: "set-pending-start", pendingStart: null });
    hook.dispatch({ type: "set-range", range: { start: null, end: null } });
    if (next === "range") {
      hook.dispatch({
        type: "set-multi-selected",
        multiSelectedKeys: new Set(),
      });
    }
  };

  const handleDateSelect = (date: LocalDate): void => {
    if (hook.state.mode === "range") {
      handleDateSelectRange(date);
      return;
    }
    if (allowRange) {
      if (!allowSingleModeMultiSelect) {
        handleDateSelectSingleInRangePicker(date);
        return;
      }
      hook.dispatch({
        type: "set-multi-selected",
        multiSelectedKeys: (() => {
          const next = new Set(hook.state.multiSelectedKeys);
          const key = toDayKey(date);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        })(),
      });
      return;
    }
    handleDateSelectSingleImmediate(date);
  };

  const restrictMonth = props.restrictToMonth
    ? normalizeToFirstOfMonth(props.restrictToMonth)
    : null;

  const isNavigationLocked =
    Boolean(restrictMonth) && Boolean(props.disableNavigationOutsideMonth);
  const isPrevDisabled = isNavigationLocked;
  const isNextDisabled = isNavigationLocked;

  const messageText =
    visualState === "error" ? resolvedErrorText : props.hintText;

  useEffect(() => {
    if (props.value) {
      hook.dispatch({
        type: "set-current-month",
        currentMonth: normalizeToFirstOfMonth(props.value),
      });
    }
  }, [props.value, hook.dispatch]);

  useEffect(() => {
    if (props.rangeOnly) {
      hook.dispatch({ type: "set-mode", mode: "range" });
    }
  }, [props.rangeOnly, hook.dispatch]);

  useEffect(() => {
    if (!hook.state.isOpen) return;
    hook.dispatch({ type: "sync-from-committed" });
  }, [hook.dispatch, hook.state.isOpen]);

  useEffect(() => {
    if (!props.disabled || !hook.state.isOpen) return;
    hook.dispatch({ type: "set-open", isOpen: false });
    hook.dispatch({ type: "set-focused", isFocused: false });
  }, [hook.dispatch, hook.state.isOpen, props.disabled]);

  useEffect(() => {
    if (!hook.state.isFocused) {
      hook.dispatch({
        type: "set-draft-value",
        draftValue: formatDateValue(props.value ?? null, dateFormat),
      });
    }
  }, [dateFormat, hook.dispatch, hook.state.isFocused, props.value]);

  useOnClickOutside(
    [hook.containerRef, hook.popupRef],
    () => {
      closePicker(true);
    },
    { enabled: hook.state.isOpen, eventType: "mousedown" },
  );

  useEffect(() => {
    if (!hook.state.isOpen) return;
    const updatePosition = (): void => {
      const rect = hook.triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const minWidth =
        size === "compact"
          ? COMPACT_POPUP_MIN_WIDTH_PX
          : DEFAULT_POPUP_MIN_WIDTH_PX;
      const width = Math.max(rect.width, minWidth);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const popupHeight = hook.popupRef.current?.offsetHeight ?? 0;
      const desiredTop = rect.bottom + 22;
      const shouldPlaceBelow = desiredTop + popupHeight <= viewportHeight - 8;
      const top = shouldPlaceBelow
        ? desiredTop
        : Math.min(
            Math.max(8, rect.top),
            Math.max(8, viewportHeight - popupHeight - 14),
          );
      const desiredLeft = shouldPlaceBelow ? rect.left - 20 : rect.right + 55;
      const left = Math.min(
        Math.max(0, desiredLeft),
        Math.max(0, viewportWidth - width - 8),
      );
      hook.dispatch({
        type: "set-menu-position",
        menuPosition: { top, left, width },
      });
    };
    updatePosition();
    requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [hook.dispatch, hook.popupRef, hook.state.isOpen, hook.triggerRef, size]);

  const menu = (
    <DatePickerPopup
      ref={hook.popupRef}
      position={hook.state.menuPosition}
      zIndex={DEFAULT_MENU_Z_INDEX}
      size={size}
      mode={hook.state.mode}
      showModeButtons={showModeButtons}
      showHolidayWeekendToggles={showHolidayWeekendToggles}
      allowRange={allowRange}
      allowClear={Boolean(props.allowClear)}
      canConfirm={canConfirm}
      currentMonth={hook.state.currentMonth}
      monthNames={calendarLabels.monthNames}
      dayHeaders={calendarLabels.dayHeaders}
      days={days}
      todayKey={todayKey}
      valueKey={valueKey}
      rangeStartKey={rangeStartKey}
      rangeEndKey={rangeEndKey}
      pendingStartKey={pendingStartKey}
      pendingStart={hook.state.pendingStart}
      multiSelectedKeys={hook.state.multiSelectedKeys}
      restrictSameMonthRange={restrictSameMonthRange}
      excludeHolidaysValue={excludeHolidaysValue}
      excludeWeekendsValue={excludeWeekendsValue}
      isControlledHoliday={isControlledHoliday}
      isControlledWeekends={isControlledWeekends}
      excludeHolidaysLabel={t("DatePicker.ExcludeHolidays")}
      excludeWeekendsLabel={t("DatePicker.ExcludeSaturdayAndSunday")}
      notSelectableLabel={t("DatePicker.NotSelectable")}
      modeLabel={t("DatePicker.Mode")}
      singleLabel={t("DatePicker.Single")}
      rangeLabel={t("DatePicker.Range")}
      clearLabel={t("DatePicker.Clear")}
      chooseLabel={t("DatePicker.Choose")}
      onToggleMode={handleToggleMode}
      onPrevMonth={() => {
        hook.dispatch({
          type: "set-current-month",
          currentMonth: addMonths(hook.state.currentMonth, -1),
        });
      }}
      onNextMonth={() => {
        hook.dispatch({
          type: "set-current-month",
          currentMonth: addMonths(hook.state.currentMonth, 1),
        });
      }}
      onSelectDay={handleDateSelect}
      onClear={handleClear}
      onConfirm={handleConfirm}
      onExcludeHolidaysChange={props.onExcludeHolidaysChange}
      onExcludeWeekendsChange={props.onExcludeWeekendsChange}
      onExcludeNonWorkingDaysChange={props.onExcludeNonWorkingDaysChange}
      onSetLocalExcludeHolidays={(checked) =>
        hook.dispatch({ type: "set-local-exclude-holidays", value: checked })
      }
      onSetLocalExcludeWeekends={(checked) =>
        hook.dispatch({ type: "set-local-exclude-weekends", value: checked })
      }
      isAllowedByMin={isAllowedByMin}
      matchesMonthlyDay={matchesMonthlyDay}
      shouldExclude={shouldExclude}
      isPrevDisabled={isPrevDisabled}
      isNextDisabled={isNextDisabled}
    />
  );

  return (
    <Container ref={hook.containerRef} $size={size}>
      <DatePickerInput
        labelText={props.label}
        required={props.required}
        size={size}
        visualState={visualState}
        isOpen={hook.state.isOpen}
        value={allowRange ? showValue : hook.state.draftValue}
        placeholderText={placeholderText}
        disabled={props.disabled}
        readOnly={allowRange}
        showClearButton={showClearButton}
        messageText={messageText}
        onClear={handleClear}
        onToggleOpen={handleToggleOpen}
        onFocus={handleFocus}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        inputRef={hook.triggerRef}
        clearLabel={t("DatePicker.Clear")}
        openLabel={t("DatePicker.Open")}
      />
      {hook.state.isOpen &&
      hook.state.menuPosition &&
      typeof document !== "undefined"
        ? createPortal(menu, document.body)
        : null}
    </Container>
  );
};
